/**
 * PM Navigation Utility
 *
 * Provides URL-based navigation for the PM module.
 * This utility allows navigation from outside React components (e.g., Zustand store).
 *
 * The navigate function is set by PMEmbeddedWorkspace on mount.
 */

import type { PMCanvas } from '../stores'

// Store the navigate function from react-router
let navigateFn: ((path: string) => void) | null = null

// Flag to prevent double navigation when URL changes trigger setActiveCanvas
let isNavigating = false

/**
 * Set the navigate function (called from PMEmbeddedWorkspace)
 */
export function setPMNavigate(fn: (path: string) => void) {
  navigateFn = fn
}

/**
 * Check if we're currently in a navigation action
 */
export function isNavigationInProgress(): boolean {
  return isNavigating
}

/**
 * Reset navigation flag (called after route effect runs)
 */
export function resetNavigationFlag() {
  isNavigating = false
}

/**
 * Map canvas to URL path
 */
function getCanvasPath(canvas: PMCanvas, entityId?: string): string {
  const base = '/dashboard/pm'

  switch (canvas) {
    // Detail views with entity IDs
    case 'task-detail':
      return entityId ? `${base}/tasks/${entityId}` : `${base}/my-tasks`
    case 'project-detail':
      return entityId ? `${base}/projects/${entityId}` : `${base}/projects`
    case 'sprint-detail':
      return entityId ? `${base}/sprints/${entityId}` : `${base}/sprints`
    case 'client-detail':
      return entityId ? `${base}/clients/${entityId}` : `${base}/clients`

    // Dashboard (root)
    case 'dashboard':
      return base

    // All other canvases map directly to URL
    default:
      return `${base}/${canvas}`
  }
}

/**
 * Navigate to a PM canvas with URL update
 */
export function pmNavigate(canvas: PMCanvas, entityId?: string) {
  if (!navigateFn) {
    console.warn('[PM Navigation] Navigate function not set yet')
    return
  }

  const path = getCanvasPath(canvas, entityId)

  // Don't navigate if we're already on the target URL
  // This prevents unnecessary navigation that could cause issues
  if (typeof window !== 'undefined' && window.location.pathname === path) {
    return
  }

  isNavigating = true
  navigateFn(path)
}

/**
 * Navigate back in PM module
 * Uses browser history for proper back navigation
 */
export function pmNavigateBack() {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    window.history.back()
  } else if (navigateFn) {
    // Fallback to dashboard if no history
    navigateFn('/dashboard/pm')
  }
}

/**
 * Get the canvas type from a URL path
 */
export function getCanvasFromPath(pathname: string): { canvas: PMCanvas; entityId?: string } | null {
  const pmMatch = pathname.match(/\/dashboard\/pm(?:\/(.*))?/)
  if (!pmMatch) return null

  const subPath = pmMatch[1] || ''
  const parts = subPath.split('/').filter(Boolean)

  if (parts.length === 0) {
    return { canvas: 'dashboard' }
  }

  // Check for detail views
  if (parts.length === 2) {
    switch (parts[0]) {
      case 'tasks':
        return { canvas: 'task-detail', entityId: parts[1] }
      case 'projects':
        return { canvas: 'project-detail', entityId: parts[1] }
      case 'sprints':
        return { canvas: 'sprint-detail', entityId: parts[1] }
      case 'clients':
        return { canvas: 'client-detail', entityId: parts[1] }
    }
  }

  // Single segment paths
  const canvasMap: Record<string, PMCanvas> = {
    'dashboard': 'dashboard',
    'manager': 'manager',
    'projects': 'projects',
    'my-projects': 'my-projects',
    'tasks': 'tasks',
    'my-tasks': 'my-tasks',
    'assigned-by-me': 'assigned-by-me',
    'sprints': 'sprints',
    'backlog': 'backlog',
    'milestones': 'milestones',
    'gantt': 'gantt',
    'timeline': 'timeline',
    'deadlines': 'deadlines',
    'calendar': 'calendar',
    'my-calendar': 'my-calendar',
    'resources': 'resources',
    'time-tracking': 'time-tracking',
    'risks': 'risks',
    'clients': 'clients',
    'analytics': 'analytics',
    'workflows': 'workflows',
    'chat': 'chat',
    'feed': 'feed',
  }

  const canvas = canvasMap[parts[0]]
  return canvas ? { canvas } : null
}
